import React, { useState } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Label,
    Input,
    Container,
    Form,
    Spinner,
    Alert,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axios from "axios";
import SDK from "../../../config";

const AddNewApp = () => {
    const [formData, setFormData] = useState({
        appId: "",
        appName: "",
        urlScheme: "",
        appIdentifier: "",
        appUrl: "",
        iconUrl: "",
        appType: "",
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);

    const validateField = (fieldName, value) => {
        let errorMsg = "";

        switch (fieldName) {
            case "appId":
                errorMsg = value.trim() === "" ? "App ID is required" : "";
                break;
            case "appName":
                errorMsg = value.trim() === "" ? "App Name is required" : "";
                break;
            case "urlScheme":
                errorMsg = !value.match(/^([a-z][a-z0-9+\-.]*):\/\//i)
                    ? "Enter a valid URL scheme (e.g., viber://)"
                    : "";
                break;
            case "appIdentifier":
                errorMsg = !value.match(/^([a-z0-9]+\.)+[a-z0-9]+$/i)
                    ? "Enter a valid App Identifier (e.g., com.example.app)"
                    : "";
                break;
            case "appUrl":
            case "iconUrl":
                errorMsg = !value.match(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i)
                    ? "Enter a valid URL"
                    : "";
                break;
            case "appType":
                errorMsg = value.trim() === "" ? "App Type is required" : "";
                break;
            default:
                break;
        }

        return errorMsg;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        const errorMsg = validateField(name, value);
        setErrors({ ...errors, [name]: errorMsg });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        Object.keys(formData).forEach((field) => {
            newErrors[field] = validateField(field, formData[field]);
        });

        setErrors(newErrors);
        const hasErrors = Object.values(newErrors).some((error) => error !== "");

        if (!hasErrors) {
            setIsSubmitting(true);
            try {
                const response = await axios.post(`${SDK.SOCKET_URL}/api/iOS/post/app/appId`, formData);

                // Assuming a successful response returns status code 200 and has a specific success field
                if (response.status === 200 && response.data.success) {
                    setSubmissionStatus({ success: true, message: "App added successfully!" });
                    setFormData({
                        appId: "",
                        appName: "",
                        urlScheme: "",
                        appIdentifier: "",
                        appUrl: "",
                        iconUrl: "",
                        appType: "",
                    });
                } else {
                    throw new Error("Unexpected response from server");
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                setSubmissionStatus({ success: false, message: "Failed to add app. Please try again later." });
            } finally {
                setIsSubmitting(false);
            }
        } else {
            console.log("Form has errors:", newErrors);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content" style={{ minHeight: "100vh" }}>
                <Container fluid={true}>
                    <Breadcrumbs
                        title="Add New App"
                        breadcrumbItems={[
                            { title: "Apps", link: "#" },
                            { title: "Add Resolve App", link: "#" },
                        ]}
                    />
                    <Row className="d-flex justify-content-center align-content-center">
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <h4 className="card-title">New App with URL Scheme</h4>
                                    <p className="card-title-desc">
                                        Add Resolve App with the <code>URL Scheme</code> here.
                                    </p>
                                    {submissionStatus && (
                                        <Alert color={submissionStatus.success ? "success" : "danger"}>
                                            {submissionStatus.message}
                                        </Alert>
                                    )}
                                    <Form onSubmit={handleSubmit}>
                                        <Row>
                                            {[
                                                { label: "App ID", name: "appId", placeholder: "Enter App ID (382617920)" },
                                                { label: "App Name", name: "appName", placeholder: "Enter App Name" },
                                                { label: "URL Scheme", name: "urlScheme", placeholder: "Enter URL Scheme (viber://)" },
                                                { label: "App Identifier", name: "appIdentifier", placeholder: "Enter App Identifier (com.viber.viber)" },
                                                { label: "App URL", name: "appUrl", placeholder: "Enter App URL" },
                                                { label: "App Type", name: "appType", placeholder: "Enter App Type (Social Networking)" },
                                            ].map((field, index) => (
                                                <Col md="6" key={index}>
                                                    <div className="mb-3 position-relative">
                                                        <Label className="form-label" htmlFor={field.name}>
                                                            {field.label}
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            id={field.name}
                                                            name={field.name}
                                                            value={formData[field.name]}
                                                            placeholder={field.placeholder}
                                                            onChange={handleChange}
                                                        />
                                                        {errors[field.name] && (
                                                            <div className="invalid-feedback d-block">
                                                                {errors[field.name]}
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            ))}
                                            <Col md="12">
                                                <div className="mb-3 position-relative">
                                                    <Label className="form-label" htmlFor="iconUrl">
                                                        Icon URL
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        id="iconUrl"
                                                        name="iconUrl"
                                                        value={formData.iconUrl}
                                                        placeholder="Enter Icon URL"
                                                        onChange={handleChange}
                                                    />
                                                    {errors.iconUrl && (
                                                        <div className="invalid-feedback d-block">
                                                            {errors.iconUrl}
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                        <Button color="primary" type="submit" disabled={isSubmitting}>
                                            {isSubmitting ? <Spinner size="sm" /> : "Add App"}
                                        </Button>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default AddNewApp;
